import { memo, type RefObject } from 'react';

import { WorkspaceMemberType } from '@air/api/types';
import { ChevronDownIcon } from '@air/design-icons';
import { Avatar } from '@air/primitive-avatar';
import { formatFullName, formatInitials } from '@air/utilities';

import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';
import { type PopperOptions, usePopperContainer } from '~/hooks/usePopperContainer';
import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

const popperOptions: PopperOptions = {
  placement: 'top',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -10],
      },
    },
  ],
};

export type UserMentionTooltipProps = {
  user: MentionsData;
  containerRef: RefObject<HTMLElement>;
};

export const UserMentionTooltip = memo(
  ({ containerRef, user: { firstName, lastName, avatar, id, type: userType, roles } }: UserMentionTooltipProps) => {
    const fullName = formatFullName(firstName, lastName);
    const initials = formatInitials(firstName, lastName);

    const { PopperContainer } = usePopperContainer({ containerRef, usePortal: true, options: popperOptions });

    const roleName =
      // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
      userType === WorkspaceMemberType.member ? roles.member?.displayName : roles?.guest?.[0]?.displayName;

    return (
      <PopperContainer>
        <div className="pointer-events-none flex flex-col items-center">
          <div className="flex items-center rounded-2 bg-white p-2 shadow-[0px_2px_8px,0px_1px_3px,0px_0px_2px_] shadow-black/20">
            <Avatar alt={fullName} size={24} src={getAvatarImageSrc(avatar, 24)} text={initials} colorSeed={id} />
            <p className="ml-2 font-medium ads:text-secondary text-14 text-gray-11">{fullName}</p>
            {!!roleName && (
              <>
                <div className="mx-1 font-medium ads:text-metadata text-14 text-gray-10">・</div>
                <div className="ads:text-metadata text-14 text-gray-10">{roleName}</div>
              </>
            )}
          </div>
          <ChevronDownIcon className="mt-[-14px] text-white drop-shadow-[0px_3px_1px_rgba(0,0,0,0.2)]" size="xl" />
        </div>
      </PopperContainer>
    );
  },
);

UserMentionTooltip.displayName = 'UserMentionTooltip';
